import React, { Suspense, lazy } from 'react';
import './i18n';
import './styles/global.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from './utils/Seo';
import LandingPage from './components/LandingPage';
import ErrorBoundary from './components/ErrorBoundary';
import Loader from './components/Loader';
import { DataProvider } from './contexts/DataContext';

// Code splitting — heavy routes load only when needed, cutting unused JS
// (admin dashboard, client offers, detail pages) out of the initial bundle.
// ProtectedRoute is lazy too because it pulls in firebase/auth (Google OAuth
// iframe), which must stay out of the public site's main bundle.
const ClientOffer = lazy(() => import('./components/ClientOffer'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const FilmsPage = lazy(() => import('./components/FilmsPage'));
const ProjectsPage = lazy(() => import('./components/ProjectsPage'));
const ProjectDetails = lazy(() => import('./components/ProjectDetails'));
const PackageDetailsPage = lazy(() => import('./components/PackageDetailsPage'));
const RentACarPage = lazy(() => import('./components/RentACarPage'));
const ForColleagues = lazy(() => import('./components/ForColleagues'));
const ReviewPage = lazy(() => import('./components/ReviewPage'));
const GuestGalleryPage = lazy(() => import('./components/GuestGalleryPage'));

const withSuspense = (element) => (
  <Suspense fallback={<Loader label="INFINITY" />}>{element}</Suspense>
);

function AppContent() {
  return (
    <>
      <Toaster 
        position="top-center"
        toastOptions={{
          className: 'font-sans',
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '0px',
          },
          duration: 4000,
        }}
      />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/za-kolege" element={withSuspense(<ForColleagues />)} />
        <Route path="/projekti" element={withSuspense(<ProjectsPage />)} />
        <Route path="/projekti/:id" element={withSuspense(<ProjectDetails />)} />
        <Route path="/paketi/:id" element={withSuspense(<PackageDetailsPage />)} />
        <Route path="/rent-a-car" element={withSuspense(<RentACarPage />)} />
        <Route path="/qr-galerija" element={withSuspense(<GuestGalleryPage />)} />
        <Route path="/offers" element={<Navigate to="/" replace />} />
        <Route path="/client/:id" element={withSuspense(<ClientOffer />)} />
        <Route path="/recenzija/:token" element={withSuspense(<ReviewPage />)} />
        <Route path="/films" element={withSuspense(<FilmsPage />)} />
        <Route path="/login" element={withSuspense(<AdminLogin />)} />
        <Route
          path="/admin/*"
          element={
            <ErrorBoundary>
              <ProtectedRoute>
                {withSuspense(<AdminDashboard />)}
              </ProtectedRoute>
            </ErrorBoundary>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <DataProvider>
          <Router>
            <AppContent />
          </Router>
        </DataProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;